import type {
  DistrictIncomeCompositeReport,
  IncomeBreakdownChurch,
  IncomeBreakdownRow,
  MoneyLeaf,
  NationIncomeBreakdownReport,
  DistrictInHouseChurch,
  DistrictInHouseRow,
} from "@core/reports/api/fetchFinanceReport";

/**
 * Adapters for the income pivots (Tab 1 / Tab 2 at district & nation).
 * Columns = dynamic KPIs (keyed by kpi_id). Rows = District→Church with measure
 * leaf rows per church. Subtotal & grand-total rows are computed here (summing the
 * church measure leaves per KPI) — ReportTreeTable can't be used because it auto-sums
 * the overlapping measure rows and would double-count (D5).
 */

export type PivotColumn = { kpi_id: number; source: string };
export type PivotMeasure = { key: string; label: string };

/** One measure row: per-KPI values keyed by kpi_id. */
export type PivotMeasureRow = { key: string; label: string; values: Record<number, number | null> };

export type PivotEntity = {
  id: string;
  name: string;
  level: "district" | "church";
  measureRows: PivotMeasureRow[];
  children?: PivotEntity[];
};

export type PivotModel = {
  columns: PivotColumn[];
  measures: PivotMeasure[];
  entities: PivotEntity[];
  grandTotal: PivotMeasureRow[];
};

/** Income breakdown measures (D5 — 4, no total_raised). */
export const INCOME_MEASURES: PivotMeasure[] = [
  { key: "total_to_church", label: "Total to Church" },
  { key: "total_out", label: "Total Out" },
  { key: "total_to_district_office", label: "Total to District Office" },
  { key: "total_to_island_office", label: "Total to Island Office" },
];

/** District in-house measures. */
export const INHOUSE_MEASURES: PivotMeasure[] = [
  { key: "funds_to_island_office", label: "Total to Island Office" },
  { key: "funds_to_district_office", label: "Total to District Office" },
];

type AnyRow = Record<string, unknown> & { kpi_id: number; source: string };

function leafValue(row: AnyRow, key: string): number | null {
  const leaf = row[key] as MoneyLeaf | undefined;
  const v = leaf?.value;
  return typeof v === "number" ? v : null;
}

/** Collect KPI columns (kpi_id → source) in first-seen order across all rows. */
function collectColumns(rowsList: AnyRow[][]): PivotColumn[] {
  const seen = new Map<number, string>();
  for (const rows of rowsList) {
    for (const row of rows) {
      if (!seen.has(row.kpi_id)) seen.set(row.kpi_id, row.source);
    }
  }
  return Array.from(seen, ([kpi_id, source]) => ({ kpi_id, source }));
}

/** Build the per-church measure rows from that church's income/in-house rows. */
function churchMeasureRows(rows: AnyRow[], measures: PivotMeasure[]): PivotMeasureRow[] {
  return measures.map((m) => {
    const values: Record<number, number | null> = {};
    for (const row of rows) values[row.kpi_id] = leafValue(row, m.key);
    return { key: m.key, label: m.label, values };
  });
}

/** Sum a list of measure-row sets into one (per measure, per KPI). */
function sumMeasureRows(rowSets: PivotMeasureRow[][], measures: PivotMeasure[], columns: PivotColumn[]): PivotMeasureRow[] {
  return measures.map((m, mi) => {
    const values: Record<number, number | null> = {};
    for (const col of columns) {
      let total = 0;
      let any = false;
      for (const set of rowSets) {
        const v = set[mi]?.values[col.kpi_id];
        if (typeof v === "number") { total += v; any = true; }
      }
      values[col.kpi_id] = any ? total : null;
    }
    return { key: m.key, label: m.label, values };
  });
}

// ── Income breakdown ────────────────────────────────────────────────────────

function incomeChurchToEntity(church: { church_id: number; church_name: string; rows: IncomeBreakdownRow[] }): { entity: PivotEntity; rows: PivotMeasureRow[] } {
  const measureRows = churchMeasureRows(church.rows as unknown as AnyRow[], INCOME_MEASURES);
  return {
    entity: { id: `church:${church.church_id}`, name: church.church_name, level: "church", measureRows },
    rows: measureRows,
  };
}

export function buildDistrictIncomePivot(report: DistrictIncomeCompositeReport | null): PivotModel {
  const churches: IncomeBreakdownChurch[] = report?.income_breakdown?.churches ?? [];
  const columns = collectColumns(churches.map((c) => c.rows as unknown as AnyRow[]));
  const entities: PivotEntity[] = [];
  const churchRowSets: PivotMeasureRow[][] = [];
  for (const c of churches) {
    const { entity, rows } = incomeChurchToEntity(c);
    entities.push(entity);
    churchRowSets.push(rows);
  }
  return { columns, measures: INCOME_MEASURES, entities, grandTotal: sumMeasureRows(churchRowSets, INCOME_MEASURES, columns) };
}

export function buildNationIncomePivot(report: NationIncomeBreakdownReport | null): PivotModel {
  const districts = report?.income_breakdown?.districts ?? [];
  const allChurchRows: AnyRow[][] = [];
  for (const d of districts) for (const c of d.churches) allChurchRows.push(c.rows as unknown as AnyRow[]);
  const columns = collectColumns(allChurchRows);

  const entities: PivotEntity[] = [];
  const allChurchRowSets: PivotMeasureRow[][] = [];
  for (const d of districts) {
    const childRowSets: PivotMeasureRow[][] = [];
    const children: PivotEntity[] = [];
    for (const c of d.churches) {
      const { entity, rows } = incomeChurchToEntity(c);
      children.push(entity);
      childRowSets.push(rows);
      allChurchRowSets.push(rows);
    }
    entities.push({
      id: `district:${d.district_id}`,
      name: d.district_name,
      level: "district",
      measureRows: sumMeasureRows(childRowSets, INCOME_MEASURES, columns),
      children,
    });
  }
  return { columns, measures: INCOME_MEASURES, entities, grandTotal: sumMeasureRows(allChurchRowSets, INCOME_MEASURES, columns) };
}

// ── District in-house ───────────────────────────────────────────────────────

export function buildDistrictInHousePivot(report: DistrictIncomeCompositeReport | null): PivotModel {
  const churches: DistrictInHouseChurch[] = report?.district_in_house_report?.churches ?? [];
  const columns = collectColumns(churches.map((c) => c.rows as unknown as AnyRow[]));
  const entities: PivotEntity[] = [];
  const churchRowSets: PivotMeasureRow[][] = [];
  for (const c of churches) {
    const measureRows = churchMeasureRows(c.rows as unknown as AnyRow[], INHOUSE_MEASURES);
    entities.push({ id: `church:${c.church_id}`, name: c.church_name, level: "church", measureRows });
    churchRowSets.push(measureRows);
  }
  return { columns, measures: INHOUSE_MEASURES, entities, grandTotal: sumMeasureRows(churchRowSets, INHOUSE_MEASURES, columns) };
}

// silence unused-import type warnings for re-exported row types used only via casts
export type { DistrictInHouseRow, IncomeBreakdownRow };
